import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Table from './Table';
import RequestLogBadge from './RequestLogBadge';
import formatDate from '../helpers/formatDate';
import showInvisibleCharacters from '../helpers/showInvisibleCharacters';

export default function BrokersTable() {
  const [ffData, setFFData] = useState([]);
  const [loadingFFData, setLoadingFFData] = useState(false);
  const [hubspotData, setHubspotData] = useState([]);
  const [loadingHubspotData, setLoadingHubspotData] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadTime, setLoadTime] = useState(null);
  const [requestLog, setRequestLog] = useState([]);
  const [requestLogModalOpen, setRequestLogModalOpen] = useState(false);
  const [emailProblemsModalOpen, setEmailProblemsModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [modalMessage, setModalMessage] = useState(null);
  const [hubspotJsonModal, setHubspotJsonModal] = useState(null);
  const [successToastMessage, setSuccessToastMessage] = useState(null);
  const [syncingRecordIds, setSyncingRecordIds] = useState([]);
  const [togglingArchiveRecordIds, setTogglingArchiveRecordIds] = useState([]);
  const [bulkSyncConfirmation, setBulkSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkSyncState, setBulkSyncState] = useState({
    isOpen: false,
    isRunning: false,
    total: 0,
    completed: 0,
    currentLabel: null,
    failures: [],
  });
  const [bulkTimestampSyncConfirmation, setBulkTimestampSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkTimestampSyncState, setBulkTimestampSyncState] = useState({
    isRunning: false,
  });
  const [syncPreview, setSyncPreview] = useState({
    isOpen: false,
    isLoading: false,
    isSaving: false,
    record: null,
    fields: null,
    isNewRecord: false,
    pendingMerges: [],
    error: null,
  });
  const [mergeOrphanConfirmation, setMergeOrphanConfirmation] = useState({ isOpen: false, isSaving: false, record: null });
  const [bulkOrphanMerge, setBulkOrphanMerge] = useState({ isOpen: false, isRunning: false, records: [] });
  const [undoMergeConfirmation, setUndoMergeConfirmation] = useState({
    isOpen: false,
    isSaving: false,
    record: null,
  });
  const activeSyncKeysRef = useRef(new Set());
  const bulkSyncRunningRef = useRef(false);
  const tableInstanceRef = useRef(null);

  // Toggles the Sync Status column filter — clicking an already-active status
  // clears it. Reads the current value at click time via the table instance
  // captured from renderToolbar, so it doesn't need filter state lifted into
  // this component just to stay in sync.
  const toggleSyncStatusFilter = useCallback((status) => {
    const column = tableInstanceRef.current?.getColumn('sync_status');
    if (!column) return;

    column.setFilterValue((prev) => (prev === status ? undefined : status));
  }, []);

  useEffect(() => {
    setLoadingFFData(true)
    async function fetchData() {
      const response = await fetch("api/brokers");
      const res = await response.json();
      return (res)
    }
    // call the function
    fetchData()
      .then((res) => {
        setFFData(res.data)
        setLoadingFFData(false)
      })
      // make sure to catch any error
      .catch(console.error);
  }, [reloadKey]);

  const refreshData = () => setReloadKey(Date.now());

  useEffect(() => {
    setLoadingHubspotData(true);
    setLoadError(null);
    setLoadTime(null);
    setRequestLog([]);
    setHubspotData([]);

    const startTime = Date.now();
    let cancelled = false;

    // Each request covers one page (100 companies) of HubSpot's own search cursor
    // rather than fetching every consultant company in one request (see
    // brokers_controller#hubspot_index) — loop until every page has been fetched,
    // accumulating into local state as each one arrives.
    async function fetchAllPages() {
      const accumulated = [];
      const log = [];
      let offset = null;
      let hasMore = true;
      let requestNumber = 0;

      while (hasMore) {
        if (cancelled) break;

        const searchParams = new URLSearchParams();
        if (offset) {
          searchParams.set('offset', offset);
        }

        requestNumber += 1;
        const currentRequestNumber = requestNumber;
        const requestStartedAt = Date.now();

        log.push({
          requestNumber: currentRequestNumber,
          offset: offset || 0,
          status: null,
          ok: null,
          pending: true,
          durationMs: null,
          returned: null,
          error: null,
        });
        if (!cancelled) setRequestLog([...log]);

        let response = null;
        let res = null;
        let errorMessage = null;

        try {
          const query = searchParams.toString();
          response = await fetch(`api/brokers/hubspot${query ? `?${query}` : ''}`);
          res = await response.json();
          if (!response.ok) {
            errorMessage = `HTTP ${response.status}`;
          }
        } catch (err) {
          errorMessage = err.message;
        }

        const entryIndex = log.findIndex((entry) => entry.requestNumber === currentRequestNumber);
        if (entryIndex !== -1) {
          log[entryIndex] = {
            requestNumber: currentRequestNumber,
            offset: offset || 0,
            status: response ? response.status : null,
            ok: !errorMessage,
            pending: false,
            durationMs: Date.now() - requestStartedAt,
            returned: res?.meta?.returned,
            error: errorMessage,
          };
        }
        if (!cancelled) setRequestLog([...log]);

        if (errorMessage) {
          throw new Error(`Failed to load HubSpot companies (${errorMessage})`);
        }

        accumulated.push(...(res.data || []));

        // Data accumulates here as pages arrive, but loadingHubspotData stays true
        // until the whole fetch finishes (see the .then()/.catch() below) — the
        // table itself only appears once everything's loaded, avoiding a
        // half-loaded sync-status comparison. The request log (rendered outside
        // the gated <Table>) is what shows progress in the meantime.
        if (!cancelled) {
          setHubspotData([...accumulated]);
        }

        offset = res.meta?.next_offset;
        hasMore = Boolean(res.meta?.has_more && offset);
      }
    }

    fetchAllPages()
      .then(() => {
        if (!cancelled) {
          setLoadTime(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadingHubspotData(false);
        }
      })
      .catch((error) => {
        console.error(error);

        if (!cancelled) {
          setLoadTime(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadError('Unable to load HubSpot company records.');
          setLoadingHubspotData(false);
        }
      });

    return () => { cancelled = true; };
  }, [reloadKey]);

  const getRecordKey = useCallback((record) => {
    if (record.ff_record?.id) return `ff-${record.ff_record.id}`;
    if (record.hs_record?.id) return `hs-${record.hs_record.id}`;
    if (record.ff_record?.hubspot_id) return `hubspot-${record.ff_record.hubspot_id}`;
    return 'unknown-record';
  }, []);

  const getRecordLabel = useCallback((record) => {
    return (
      record.ff_record?.display_name ||
      record.hs_record?.properties?.name ||
      record.ff_record?.email ||
      record.hs_record?.properties?.email ||
      record.ff_record?.hubspot_id ||
      record.hs_record?.id ||
      'Unknown broker'
    );
  }, []);

  const canSyncRecord = useCallback((record) => {
    return (
      ['synced', 'out_of_sync', 'out_of_sync_timestamp_only', 'pending_merge'].includes(record.sync_status) ||
      (record.sync_status === 'hs_only' && !record.has_duplicate_records)
    );
  }, []);

  const mergeUpdatedRecord = useCallback((updatedRecord) => {
    const updatedRecordId = updatedRecord.id?.toString();
    const updatedRecordHubspotId = updatedRecord.hubspot_id?.toString();

    setFFData((prev) => {
      let matched = false;

      const next = prev.map((broker) => {
        const brokerId = broker.id?.toString();
        const brokerHubspotId = broker.hubspot_id?.toString();
        const sameRecord =
          (updatedRecordId && brokerId === updatedRecordId) ||
          (updatedRecordHubspotId && brokerHubspotId === updatedRecordHubspotId);

        if (sameRecord) {
          matched = true;
          return {
            ...broker,
            ...updatedRecord,
            user: updatedRecord.user || broker.user,
            agency: updatedRecord.agency || broker.agency,
          };
        }

        return broker;
      });

      return matched ? next : [...next, updatedRecord];
    });
  }, []);

  const performSyncRequest = useCallback(async (record, options = {}) => {
    const { showSuccessToast = false, showErrorModal = false } = options;

    try {
      const response = await fetch(`api/brokers/${record.hs_record.id}/sync_with_hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content') },
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);

        if (responseData?.data) {
          const updatedRecord = typeof responseData.data === 'string'
            ? JSON.parse(responseData.data)
            : responseData.data;

          mergeUpdatedRecord(updatedRecord);
        }

        // Duplicates folded into this broker by the sync (now archived + merged_into_id).
        const mergedBrokers = responseData?.merged_brokers || [];
        mergedBrokers.forEach(mergeUpdatedRecord);

        if (showSuccessToast) {
          setSuccessToastMessage(mergedBrokers.length > 0
            ? `The broker was synced with HubSpot and ${mergedBrokers.length} duplicate broker${mergedBrokers.length === 1 ? ' was' : 's were'} merged into it.`
            : 'The broker was successfully synced with HubSpot.');
        }

        return { success: true };
      } else {
        console.log('Error response data:', response);
        const errorData = await response.json().catch(() => null);
        const message = errorData?.errors?.join('\n') || `Sync failed (HTTP ${response.status})`;
        if (showErrorModal) {
          setModalMessage(message);
        }

        return { success: false, message };
      }
    } catch (error) {
      console.error("Sync error:", error);
      const message = 'An unexpected error occurred while syncing.';

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    }
  }, [mergeUpdatedRecord]);

  const sendSyncRequest = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return null;

    const recordKey = getRecordKey(record);
    if (activeSyncKeysRef.current.has(recordKey)) return null;

    activeSyncKeysRef.current.add(recordKey);
    setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));

    try {
      return await performSyncRequest(record, { showSuccessToast: true, showErrorModal: true });
    } finally {
      activeSyncKeysRef.current.delete(recordKey);
      setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
    }
  }, [getRecordKey, performSyncRequest]);

  const openSyncPreview = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return;

    setSyncPreview({
      isOpen: true,
      isLoading: true,
      isSaving: false,
      record,
      fields: null,
      isNewRecord: false,
      pendingMerges: [],
      error: null,
    });

    try {
      const response = await fetch(`api/brokers/${record.hs_record.id}/sync_preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const message = responseData?.errors?.join('\n') || `Failed to load preview (HTTP ${response.status})`;
        setSyncPreview((prev) => ({ ...prev, isLoading: false, error: message }));
        return;
      }

      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        fields: responseData?.fields || [],
        isNewRecord: Boolean(responseData?.is_new_record),
        pendingMerges: responseData?.pending_merges || [],
      }));
    } catch (error) {
      console.error('Sync preview error:', error);
      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        error: 'An unexpected error occurred while loading the preview.',
      }));
    }
  }, []);

  const closeSyncPreview = useCallback(() => {
    setSyncPreview((prev) => (prev.isSaving ? prev : {
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, pendingMerges: [], error: null,
    }));
  }, []);

  const confirmSyncPreview = useCallback(async () => {
    const record = syncPreview.record;
    if (!record) return;

    setSyncPreview((prev) => ({ ...prev, isSaving: true }));
    await sendSyncRequest(record);
    setSyncPreview({
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, pendingMerges: [], error: null,
    });
  }, [syncPreview.record, sendSyncRequest]);

  const toggleArchivedStatus = useCallback(async (record) => {
    if (!record.ff_record?.id) return null;

    const recordKey = getRecordKey(record);
    if (activeSyncKeysRef.current.has(recordKey)) return null;

    activeSyncKeysRef.current.add(recordKey);
    setTogglingArchiveRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));

    try {
      const response = await fetch(`api/brokers/${record.ff_record.id}/toggle_archived`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content') },
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);

        if (responseData?.data) {
          const updatedRecord = typeof responseData.data === 'string'
            ? JSON.parse(responseData.data)
            : responseData.data;

          mergeUpdatedRecord(updatedRecord);
          setSuccessToastMessage(`Broker ${updatedRecord.archived ? 'archived' : 'unarchived'} successfully.`);
        }

        return { success: true };
      }

      const errorData = await response.json().catch(() => null);
      const message = errorData?.errors?.join('\n') || `Archive toggle failed (HTTP ${response.status})`;
      setModalMessage(message);
      return { success: false, message };
    } catch (error) {
      console.error('Archive toggle error:', error);
      const message = 'An unexpected error occurred while toggling archive status.';
      setModalMessage(message);
      return { success: false, message };
    } finally {
      activeSyncKeysRef.current.delete(recordKey);
      setTogglingArchiveRecordIds((prev) => prev.filter((id) => id !== recordKey));
    }
  }, [getRecordKey, mergeUpdatedRecord]);

  const openMergeOrphanConfirmation = useCallback((record) => {
    if (!record.orphan_of) return;
    setMergeOrphanConfirmation({ isOpen: true, isSaving: false, record });
  }, []);

  const closeMergeOrphanConfirmation = useCallback(() => {
    setMergeOrphanConfirmation((prev) => (prev.isSaving ? prev : { isOpen: false, isSaving: false, record: null }));
  }, []);

  // Soft-merges an orphaned broker into the live broker with the same email (see
  // merge_orphan) — same merge as a HubSpot-driven one, so Undo Merge reverses it.
  const confirmMergeOrphan = useCallback(async () => {
    const record = mergeOrphanConfirmation.record;
    if (!record?.ff_record?.id || !record.orphan_of) return;

    setMergeOrphanConfirmation((prev) => ({ ...prev, isSaving: true }));

    try {
      const response = await fetch(`api/brokers/${record.ff_record.id}/merge_orphan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ primary_id: record.orphan_of.id }),
      });
      const responseData = await response.json().catch(() => null);

      if (response.ok) {
        (responseData?.data || []).forEach(mergeUpdatedRecord);
        setSuccessToastMessage(`Merged FF #${record.ff_record.id} into FF #${record.orphan_of.id}.`);
      } else {
        setModalMessage(responseData?.errors?.join('\n') || `Merge failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Merge orphan error:', error);
      setModalMessage('An unexpected error occurred while merging the broker.');
    } finally {
      setMergeOrphanConfirmation({ isOpen: false, isSaving: false, record: null });
    }
  }, [mergeOrphanConfirmation.record, mergeUpdatedRecord]);

  const openBulkOrphanMerge = useCallback((records) => {
    if (records.length === 0) return;
    setBulkOrphanMerge({ isOpen: true, isRunning: false, records });
  }, []);

  const closeBulkOrphanMerge = useCallback(() => {
    setBulkOrphanMerge((prev) => (prev.isRunning ? prev : { isOpen: false, isRunning: false, records: [] }));
  }, []);

  // merge_orphan for every orphaned record matching the current filters, in one request.
  // Each merge is its own BrokerMerge, so any of them can be undone individually.
  const confirmBulkOrphanMerge = useCallback(async () => {
    const records = bulkOrphanMerge.records;
    if (records.length === 0) return;

    setBulkOrphanMerge((prev) => ({ ...prev, isRunning: true }));

    try {
      const response = await fetch('api/brokers/bulk_merge_orphans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          pairs: records.map((record) => ({ id: record.ff_record.id, primary_id: record.orphan_of.id })),
        }),
      });
      const responseData = await response.json().catch(() => null);

      if (response.ok) {
        (responseData?.data || []).forEach(mergeUpdatedRecord);
        const failed = responseData?.failed || [];
        const mergedCount = records.length - failed.length;
        if (mergedCount > 0) setSuccessToastMessage(`Merged ${mergedCount} orphaned ${mergedCount === 1 ? 'broker' : 'brokers'}.`);
        if (failed.length > 0) {
          setModalMessage(`${failed.length} could not be merged:\n${failed.map((f) => `FF #${f.id}: ${f.error}`).join('\n')}`);
        }
      } else {
        setModalMessage(responseData?.errors?.join('\n') || `Bulk merge failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Bulk orphan merge error:', error);
      setModalMessage('An unexpected error occurred while merging orphaned brokers.');
    } finally {
      setBulkOrphanMerge({ isOpen: false, isRunning: false, records: [] });
    }
  }, [bulkOrphanMerge.records, mergeUpdatedRecord]);

  const openUndoMergeConfirmation = useCallback((record) => {
    if (!record.ff_record?.merged_into_id) return;
    setUndoMergeConfirmation({ isOpen: true, isSaving: false, record });
  }, []);

  const closeUndoMergeConfirmation = useCallback(() => {
    setUndoMergeConfirmation((prev) => (prev.isSaving ? prev : { isOpen: false, isSaving: false, record: null }));
  }, []);

  // Reverses a soft merge (see Broker::UndoMerge): moves the duplicate's leads/sales/etc.
  // back off the surviving broker and restores the duplicate. Both brokers come back in
  // the response so their rows update in place.
  const confirmUndoMerge = useCallback(async () => {
    const record = undoMergeConfirmation.record;
    if (!record?.ff_record?.id) return;

    setUndoMergeConfirmation((prev) => ({ ...prev, isSaving: true }));

    try {
      const response = await fetch(`api/brokers/${record.ff_record.id}/undo_merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      });
      const responseData = await response.json().catch(() => null);

      if (response.ok) {
        (responseData?.data || []).forEach(mergeUpdatedRecord);
        setSuccessToastMessage('Merge undone successfully.');
      } else {
        setModalMessage(responseData?.errors?.join('\n') || `Undo merge failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Undo merge error:', error);
      setModalMessage('An unexpected error occurred while undoing the merge.');
    } finally {
      setUndoMergeConfirmation({ isOpen: false, isSaving: false, record: null });
    }
  }, [undoMergeConfirmation.record, mergeUpdatedRecord]);

  const closeBulkSyncModal = useCallback(() => {
    setBulkSyncState((prev) => {
      if (prev.isRunning) return prev;

      return {
        ...prev,
        isOpen: false,
        currentLabel: null,
      };
    });
  }, []);

  const openBulkSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkSyncConfirmation = useCallback(() => {
    setBulkSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  const startBulkSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkSyncState({
      isOpen: true,
      isRunning: true,
      total: records.length,
      completed: 0,
      currentLabel: null,
      failures: [],
    });

    let failureCount = 0;

    try {
      for (const record of records) {
        const recordKey = getRecordKey(record);
        const recordLabel = getRecordLabel(record);

        activeSyncKeysRef.current.add(recordKey);
        setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));
        setBulkSyncState((prev) => ({
          ...prev,
          currentLabel: recordLabel,
        }));

        const result = await performSyncRequest(record);

        if (!result?.success) {
          failureCount += 1;
          setBulkSyncState((prev) => ({
            ...prev,
            failures: [...prev.failures, {
              id: recordKey,
              label: recordLabel,
              message: result?.message || 'Unknown sync failure.',
            }],
          }));
        }

        activeSyncKeysRef.current.delete(recordKey);
        setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
        setBulkSyncState((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }
    } finally {
      bulkSyncRunningRef.current = false;
      setBulkSyncState((prev) => ({
        ...prev,
        isRunning: false,
        currentLabel: null,
      }));

      setSuccessToastMessage(`Bulk sync finished. ${records.length - failureCount} succeeded, ${failureCount} failed.`);
    }
  }, [getRecordKey, getRecordLabel, performSyncRequest]);

  const confirmBulkSync = useCallback(async () => {
    const records = bulkSyncConfirmation.records;
    closeBulkSyncConfirmation();
    await startBulkSync(records);
  }, [bulkSyncConfirmation.records, closeBulkSyncConfirmation, startBulkSync]);

  const openBulkTimestampSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkTimestampSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkTimestampSyncConfirmation = useCallback(() => {
    setBulkTimestampSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  // Sends every record's already-loaded hs_record in one request instead of one
  // request per record. The backend re-verifies (server-side) that each record is
  // still timestamp-only before writing anything, and — unlike #sync_with_hubspot —
  // touches only the hs_lastmodifieddate column, not Broker::CreateOrUpdate's full
  // field set, so this can't create an Agency, merge/destroy a duplicate broker, or
  // send an invite email.
  const startBulkTimestampSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkTimestampSyncState({ isRunning: true });

    try {
      const response = await fetch('api/brokers/bulk_sync_timestamps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_records: records.map((record) => record.hs_record) }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        setModalMessage(responseData?.errors?.join('\n') || `Timestamp sync failed (HTTP ${response.status})`);
        return;
      }

      const results = responseData?.results || [];
      const succeeded = results.filter((result) => result.success);
      const failed = results.filter((result) => !result.success);

      const timestampByHubspotId = new Map(
        succeeded.map((result) => [result.hubspot_id?.toString(), result.hs_lastmodifieddate])
      );

      if (timestampByHubspotId.size > 0) {
        setFFData((prev) => prev.map((broker) => {
          const newTimestamp = timestampByHubspotId.get(broker.hubspot_id?.toString());
          return newTimestamp ? { ...broker, hs_lastmodifieddate: newTimestamp } : broker;
        }));
      }

      if (failed.length > 0) {
        setModalMessage(
          `${succeeded.length} timestamp(s) updated. ${failed.length} were skipped:\n` +
          failed.map((result) => `#${result.hubspot_id}: ${result.error}`).join('\n')
        );
      } else {
        setSuccessToastMessage(`${succeeded.length} timestamp(s) synced with HubSpot.`);
      }
    } catch (error) {
      console.error('Bulk timestamp sync error:', error);
      setModalMessage('An unexpected error occurred while syncing timestamps.');
    } finally {
      bulkSyncRunningRef.current = false;
      setBulkTimestampSyncState({ isRunning: false });
    }
  }, []);

  const confirmBulkTimestampSync = useCallback(async () => {
    const records = bulkTimestampSyncConfirmation.records;
    closeBulkTimestampSyncConfirmation();
    await startBulkTimestampSync(records);
  }, [bulkTimestampSyncConfirmation.records, closeBulkTimestampSyncConfirmation, startBulkTimestampSync]);

  useEffect(() => {
    if (!successToastMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessToastMessage(null);
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successToastMessage]);

  const humanizeDiffKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDiffValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">—</span>;
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : <span className="text-muted">—</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const calculateSyncStatus = (ff_record, hs_record, pendingMergeRecords = [], orphanOf = null) => {
    // Soft-merged into another broker (see Broker::MergeDuplicates) — archived, kept
    // only so the merge can be undone.
    if (ff_record?.merged_into_id) return 'merged';
    // HubSpot merged other companies into this one and those still exist as separate
    // brokers here. Checked before the timestamp comparison — the timestamps can match
    // while the merge itself never ran.
    if (hs_record && pendingMergeRecords.length > 0) return 'pending_merge';
    // Its HubSpot company is gone (deleted, or no longer a consultant) but another active
    // broker with the same email is still synced — a leftover duplicate to merge into it.
    if (!hs_record && orphanOf) return 'orphaned';
    if (!hs_record  && ff_record) return 'ff_only';
    if (hs_record && !ff_record) return 'hs_only';
    const hs_value = formatDate(hs_record?.updatedAt);
    const ff_value = formatDate(ff_record?.hs_lastmodifieddate);
    if (hs_value === ff_value) return 'synced';
    // Set server-side (see flag_timestamp_only_diffs): true when hs_lastmodifieddate
    // is the only thing that changed — HubSpot bumps it for lots of things we don't
    // track, so this isn't necessarily worth acting on the way a real change is.
    if (hs_record?.timestamp_only_diff) return 'out_of_sync_timestamp_only';
    return 'out_of_sync';
  }

  // Precomputed lookup maps so the per-record work below is O(1) instead of
  // scanning the full ffData/hubspotData arrays for every single record (which,
  // with hubspotData now routinely in the thousands, made every render — not just
  // data changes, since none of this was memoized — visibly slow). Composite keys
  // use JSON.stringify rather than string concatenation so an email or agency name
  // that happens to contain the same characters as a delimiter can't collide.
  const lookupMaps = React.useMemo(() => {
    const hubspotById = new Map();
    const hubspotByMergedId = new Map();
    const hubspotByEmail = new Map();
    const ffByEmail = new Map();
    // Case-insensitive, like the backend's lower(email) matching in Broker::CreateOrUpdate.
    const ffByLowerEmail = new Map();
    const ffByEmailAndAgency = new Map();
    // Only brokers a sync would still merge — mirrors Broker::MergeDuplicates.pending_for.
    const mergeableFFByHubspotId = new Map();

    hubspotData.forEach((hs) => {
      const id = hs.id?.toString();
      if (id) hubspotById.set(id, hs);

      (hs.properties?.hs_merged_object_ids?.split(';') || []).forEach((mergedId) => {
        if (!mergedId || hubspotByMergedId.has(mergedId)) return;
        hubspotByMergedId.set(mergedId, hs);
      });

      const email = hs.properties?.email;
      if (!hubspotByEmail.has(email)) hubspotByEmail.set(email, []);
      hubspotByEmail.get(email).push(hs);
    });

    ffData.forEach((broker) => {
      if (!ffByEmail.has(broker.email)) ffByEmail.set(broker.email, []);
      ffByEmail.get(broker.email).push(broker);

      const lowerEmail = broker.email?.toLowerCase();
      if (lowerEmail) {
        if (!ffByLowerEmail.has(lowerEmail)) ffByLowerEmail.set(lowerEmail, []);
        ffByLowerEmail.get(lowerEmail).push(broker);
      }

      const agencyKey = JSON.stringify([broker.email, broker.agency?.name]);
      if (!ffByEmailAndAgency.has(agencyKey)) ffByEmailAndAgency.set(agencyKey, []);
      ffByEmailAndAgency.get(agencyKey).push(broker);

      const hubspotId = broker.hubspot_id?.toString();
      if (hubspotId && !broker.merged_into_id && !broker.skip_hubspot_merge) {
        if (!mergeableFFByHubspotId.has(hubspotId)) mergeableFFByHubspotId.set(hubspotId, []);
        mergeableFFByHubspotId.get(hubspotId).push(broker);
      }
    });

    return { hubspotById, hubspotByMergedId, hubspotByEmail, ffByEmail, ffByLowerEmail, ffByEmailAndAgency, mergeableFFByHubspotId };
  }, [ffData, hubspotData]);

  // For an unmerged FF broker whose HubSpot company no longer comes back, the active broker
  // with the same email that is still matched to a live HubSpot company, if any. Archived
  // orphans still count — archiving leaves their leads/sales/etc. behind, so they stay
  // flagged until merged.
  const findOrphanOf = (broker, hasHsMatch, hubspotById, ffByLowerEmail) => {
    if (hasHsMatch || broker.merged_into_id) return null;
    const sameEmail = ffByLowerEmail.get(broker.email?.toLowerCase()) || [];
    return sameEmail.find((other) =>
      other.id !== broker.id &&
      !other.archived &&
      !other.merged_into_id &&
      hubspotById.has(other.hubspot_id?.toString())
    ) || null;
  };

  // FF brokers a sync of this HubSpot company would merge into excludeBrokerId.
  const findPendingMergeRecords = (hs_record, excludeBrokerId, mergeableFFByHubspotId) => {
    const mergedIds = hs_record?.properties?.hs_merged_object_ids?.split(';') || [];
    return mergedIds
      .flatMap((mergedId) => mergeableFFByHubspotId.get(mergedId.trim()) || [])
      .filter((broker) => broker.id !== excludeBrokerId);
  };

  // Iterate FF data and try to find a matching hubspot record by HS ID.
  // If no direct match, check if the FF record's hubspot_id is part of any merged records in Hubspot
  const ffDataWithHsRecord = React.useMemo(() => {
    const { hubspotById, hubspotByMergedId, hubspotByEmail, ffByEmail, ffByLowerEmail, ffByEmailAndAgency, mergeableFFByHubspotId } = lookupMaps;

    const ffById = new Map(ffData.map((broker) => [broker.id, broker]));

    return ffData.map((broker) => {
      const brokerHubspotId = broker.hubspot_id?.toString();
      // Attempt to find a direct match in Hubspot data using the hubspot_id from FF record
      const matchingHubspotData = brokerHubspotId ? hubspotById.get(brokerHubspotId) : undefined;
      // If no direct match, check if broker's hubspot_id is part of any merged records
      const hsParentRecord = matchingHubspotData
        ? null
        : (brokerHubspotId && hubspotByMergedId.get(brokerHubspotId)) || null;

      // Attempt to find duplicate FF records with the same agency name and email combination
      const duplicateFFRecords = ffByEmailAndAgency.get(JSON.stringify([broker.email, broker.agency?.name])) || [];
      const ffDuplicateEmailRecords = ffByEmail.get(broker.email) || [];
      const hsDuplicateEmailRecords = hubspotByEmail.get(broker.email) || [];
      const duplicateEmailRecords = [...new Set([...ffDuplicateEmailRecords, ...hsDuplicateEmailRecords])];
      const pendingMergeRecords = findPendingMergeRecords(matchingHubspotData, broker.id, mergeableFFByHubspotId);
      // HS-merged records already get the "HS Merged" badge, so they aren't orphans.
      const orphanOf = findOrphanOf(broker, Boolean(matchingHubspotData || hsParentRecord), hubspotById, ffByLowerEmail);

      return {
        ff_record: broker,
        hs_record: matchingHubspotData,
        hs_merged_parent: hsParentRecord,
        merged_into_record: broker.merged_into_id ? ffById.get(broker.merged_into_id) || null : null,
        pending_merge_records: pendingMergeRecords,
        orphan_of: orphanOf,
        has_duplicate_records: duplicateFFRecords.length > 1,
        has_duplicate_emails: duplicateEmailRecords.length > 1,
        sync_status: calculateSyncStatus(broker, matchingHubspotData, pendingMergeRecords, orphanOf),
      };
    });
  }, [ffData, lookupMaps]);

  const combinedData = React.useMemo(() => {
    const { hubspotByEmail, ffByEmail, ffByEmailAndAgency, mergeableFFByHubspotId } = lookupMaps;
    const matchingHsIds = new Set(ffData.map((r) => r.hubspot_id?.toString()));
    const unmatchedHubspotData = hubspotData.filter((hs) => !matchingHsIds.has(hs.id?.toString()));

    const formattedUnmatchedHubspotData = unmatchedHubspotData.map((hs) => {
      const email = hs.properties?.email;
      const duplicateFFRecords = ffByEmailAndAgency.get(JSON.stringify([email, hs.properties?.network])) || [];
      const ffDuplicateEmailRecords = ffByEmail.get(email) || [];
      const hsDuplicateEmailRecords = hubspotByEmail.get(email) || [];
      const duplicateEmailRecords = [...new Set([...ffDuplicateEmailRecords, ...hsDuplicateEmailRecords])];
      const pendingMergeRecords = findPendingMergeRecords(hs, null, mergeableFFByHubspotId);
      return {
        ff_record: null,
        hs_record: hs,
        hs_merged_parent: null,
        merged_into_record: null,
        pending_merge_records: pendingMergeRecords,
        has_duplicate_records: duplicateFFRecords.length > 1,
        has_duplicate_emails: duplicateEmailRecords.length > 1,
        sync_status: calculateSyncStatus(null, hs, pendingMergeRecords),
      };
    });

    return [...formattedUnmatchedHubspotData, ...ffDataWithHsRecord];
  }, [ffData, hubspotData, ffDataWithHsRecord, lookupMaps]);
  // One entry per active broker with an email the mail server would reject. HubSpot is
  // the source of truth, so its email (EmailAddress.address_list_problems) is what's checked
  // whenever the broker has a HubSpot record. The Fastlane copy (Broker#email_problems)
  // only matters when HubSpot is already correct — a re-sync fixes it — or there's no
  // HubSpot record. Archived brokers are left out; they aren't emailed.
  const emailProblemRows = React.useMemo(() => combinedData.flatMap((row) => {
    const broker = row.ff_record;
    if (!broker || broker.archived) return [];

    const hubspotProblems = row.hs_record?.email_problems || [];
    if (hubspotProblems.length > 0) return [{ row, fix: 'hubspot', problems: hubspotProblems }];

    const fastlaneProblems = broker.email_problems || [];
    if (fastlaneProblems.length > 0) {
      return [{ row, fix: row.hs_record ? 'resync' : 'no_hubspot_record', problems: fastlaneProblems }];
    }
    return [];
  }), [combinedData]);

  const statusCounts = React.useMemo(() => {
    return combinedData.reduce((acc, row) => {
      acc[row.sync_status] = (acc[row.sync_status] || 0) + 1;
      return acc;
    }, {});
  }, [combinedData]);
  const bulkProgressPercent = bulkSyncState.total === 0
    ? 0
    : Math.round((bulkSyncState.completed / bulkSyncState.total) * 100);

  const columns = React.useMemo(
    () => [
      {
        accessorFn: (row) => row.ff_record?.id?.toString() || '',
        header: 'FF ID',
      },
      {
        accessorFn: (row) => row.hs_record?.id?.toString() || '',
        cell: (info) => (
          <div className='flex gap-2' style={{ width: info.row.original.hs_merged_parent ? '150px' : 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{info.getValue()}</span>
            {info.row.original.hs_merged_parent && (
              <span className="badge bg-danger" style={{ color: 'white' }}>
                <div>HS Merged</div>
                <div>{info.row.original.hs_merged_parent.id}</div>
              </span>
            )}
            {info.row.original.ff_record?.merged_into_id && (
              <span className="badge bg-secondary" style={{ color: 'white' }} title="Merged into this Fastlane broker">
                <div>FF Merged Into</div>
                <div>#{info.row.original.ff_record.merged_into_id}</div>
              </span>
            )}
            {info.row.original.orphan_of && (
              <span className="badge bg-danger" style={{ color: 'white' }} title="Leftover duplicate of this Fastlane broker — hover the Sync Status for details">
                <div>Orphan Of</div>
                <div>#{info.row.original.orphan_of.id}</div>
              </span>
            )}
            {info.row.original.ff_record?.skip_hubspot_merge && (
              <span className="badge bg-warning text-dark" title="A merge of this broker was undone, so syncs no longer merge it">
                Merge undone
              </span>
            )}
          </div>
        ),
        header: 'HS ID',
      },
      {
        accessorFn: (row) => formatDate(row.ff_record?.hs_lastmodifieddate),
        header: 'FF Version Timestamp',
      },
      {
        accessorFn: (row) => formatDate(row.hs_record?.updatedAt),
        header: 'HS Version Timestamp',
      },
      {
        accessorFn: (row) => formatDate(row.hs_record?.createdAt),
        header: 'HS Created At Timestamp',
      },
      {
        accessorKey: 'sync_status',
        header: 'Sync Status',
        cell: (info) => {
          const pending = info.row.original.pending_merge_records || [];
          if (info.getValue() === 'orphaned') {
            const { ff_record, orphan_of } = info.row.original;
            return (
              <span
                title={`HubSpot company ${ff_record.hubspot_id} no longer exists (deleted, or no longer a consultant). FF #${orphan_of.id} has the same email and is still synced to HubSpot company ${orphan_of.hubspot_id}, so this broker is a leftover duplicate. Merge it into #${orphan_of.id} to move its leads/sales/etc. there and archive it.`}
              >
                orphaned
              </span>
            );
          }
          if (info.getValue() !== 'pending_merge') return info.getValue();
          return (
            <span title={`Will merge FF ${pending.map((broker) => `#${broker.id}`).join(', ')} into this broker on sync`}>
              pending_merge ({pending.length})
            </span>
          );
        },
        // Exact match — the default filter fn does substring matching.
        filterFn: 'equalsString',
        meta: { filterVariant: 'select'},
      },
      {
        accessorKey: 'hs_record.properties.hs_merged_object_ids',
        accessorFn: (row) => row.hs_record?.properties?.hs_merged_object_ids || '',
        header: 'HS Merged IDs',
      },
      {
        accessorKey: 'ff_record.hs_merged_object_ids',
        accessorFn: (row) => row.ff_record?.hs_merged_object_ids || '',
        header: 'FF Merged IDs', 
      },
      {
        accessorFn: (row) => row.ff_record?.user?.email || '',
        header: 'FF User Email',
      },
      {
        accessorFn: (row) => row.ff_record?.archived ? 'Inactive' : 'Active',
        cell: (info) => {
          const isArchived = Boolean(info.row.original.ff_record?.archived);

          return (
            <span
              className="badge"
              style={{
                backgroundColor: isArchived ? 'rgba(220, 53, 69, 0.12)' : '#e9ecef',
                color: isArchived ? '#b02a37' : '#495057',
                border: `1px solid ${isArchived ? 'rgba(220, 53, 69, 0.3)' : '#ced4da'}`,
                borderRadius: '3px',
              }}
            >
              {isArchived ? 'Inactive' : 'Not Archived'}
            </span>
          );
        },
        header: 'FF Archived',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => row.ff_record?.email || row.hs_record?.properties?.email,
        header: 'Broker Email',
      },
      {
        accessorKey: 'has_duplicate_emails',
        accessorFn: (row) => row.has_duplicate_emails ? 'Yes' : 'No',
        header: 'Duplicate Emails',
        meta: { filterVariant: 'select' },
      },
      {
        accessorKey: 'has_duplicate_records',
        accessorFn: (row) => row.has_duplicate_records ? 'Yes' : 'No',
        header: 'Duplicate Records',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => row.ff_record?.display_name || row.hs_record?.properties?.name,
        header: 'Name',
      },
      {
        accessorFn: (row) => row.ff_record?.agency?.name || row.hs_record?.properties?.network,
        header: 'Agency',
        meta: { filterVariant: 'select'},
      },
      {
        accessorFn: (row) => row.hs_record?.properties?.consultant || '',
        header: 'HS Consultant',
        meta: { filterVariant: 'select' },
      },
            {
        accessorFn: (row) => row.hs_record?.properties?.consultant_name || '',
        header: 'HS Consultant Name',
        meta: { filterVariant: 'select' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const record = info.row.original;
          const isRowSyncing = syncingRecordIds.includes(getRecordKey(record));
          const isTogglingArchived = togglingArchiveRecordIds.includes(getRecordKey(record));
          // Merged rows get Undo Merge instead — unarchiving one directly would leave its
          // moved leads/sales on the surviving broker.
          if (record.ff_record?.merged_into_id) {
            return (
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => openUndoMergeConfirmation(record)}
                disabled={bulkSyncState.isRunning || undoMergeConfirmation.isSaving}
              >
                Undo Merge
              </button>
            );
          }
          const canToggleArchived = Boolean(record.ff_record?.id);
          const archiveButton = canToggleArchived ? (
            <button
              className={`btn btn-sm ${record.ff_record?.archived ? 'btn-outline-warning' : 'btn-outline-danger'}`}
              onClick={() => toggleArchivedStatus(record)}
              disabled={bulkSyncState.isRunning || isRowSyncing || isTogglingArchived}
            >
              {isTogglingArchived
                ? 'Saving...'
                : record.ff_record?.archived ? 'Unarchive' : 'Archive'}
            </button>
          ) : null;

          if (canSyncRecord(record)) {
            return (
              <div style={{ display: 'flex', gap: '4px' }}>
                {archiveButton}
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openSyncPreview(record)}
                  disabled={bulkSyncState.isRunning || isRowSyncing}
                >
                  {isRowSyncing ? 'Syncing...' : 'Sync Record'}
                </button>
                {record.hs_record && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setHubspotJsonModal(record.hs_record)}
                  >
                    View HubSpot JSON
                  </button>
                )}
              </div>
            );
          }
          if (record.sync_status === 'orphaned') {
            return (
              <div style={{ display: 'flex', gap: '4px' }}>
                {archiveButton}
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openMergeOrphanConfirmation(record)}
                  disabled={bulkSyncState.isRunning || isTogglingArchived || mergeOrphanConfirmation.isSaving}
                  title="Move this broker's leads, sales, territory checks and sent emails to the live broker, then archive this one (can be undone)"
                >
                  Merge into #{record.orphan_of.id}
                </button>
              </div>
            );
          }
          return archiveButton;
        }
      }],
    [bulkSyncState.isRunning, canSyncRecord, getRecordKey, mergeOrphanConfirmation.isSaving, openMergeOrphanConfirmation, openSyncPreview, openUndoMergeConfirmation, syncingRecordIds, toggleArchivedStatus, togglingArchiveRecordIds, undoMergeConfirmation.isSaving],
  )

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {requestLog.some((entry) => entry.pending) ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Loading...
            </div>
          ) : loadTime ? (
            <>
              <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
                Loaded in {loadTime}s
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ height: '30px' }}
                onClick={refreshData}
              >
                Reload Data
              </button>
            </>
          ) : null}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {requestLog.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setRequestLogModalOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#0d6efd',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
              </button>
              <RequestLogBadge requestLog={requestLog} />
            </div>
          )}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {loadingFFData || loadingHubspotData ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Checking broker emails...
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {emailProblemRows.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setEmailProblemsModalOpen(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Invalid emails ({emailProblemRows.length} broker{emailProblemRows.length === 1 ? '' : 's'})
                </button>
              ) : (
                <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>Invalid emails</div>
              )}
              {/* Inline colors for the same reason as RequestLogBadge — the app stylesheet
                  overrides Bootstrap's bg-* utility classes. */}
              {emailProblemRows.length > 0 ? (
                <span className="badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>
                  {emailProblemRows.length} to fix
                </span>
              ) : (
                <span className="badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>All valid</span>
              )}
            </div>
          )}
        </div>
      </div>

      {emailProblemsModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    Invalid emails ({emailProblemRows.length} broker{emailProblemRows.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setEmailProblemsModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <p className="text-muted" style={{ fontSize: '13px' }}>
                    These active brokers have an email the mail server would reject. HubSpot is the
                    source of truth, so fix the email on the HubSpot company record, then sync the broker
                    here. An invalid main email stops that broker's emails entirely (e.g. the weekly state
                    registration recap); an invalid cc is dropped from the email.
                  </p>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>FF ID</th>
                          <th>HS ID</th>
                          <th>Name</th>
                          <th>Value</th>
                          <th>Problem</th>
                          <th>How to fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailProblemRows.flatMap(({ row, fix, problems }) => problems.map((problem, index) => (
                          <tr key={`${row.ff_record.id}-${index}`}>
                            <td>{row.ff_record.id}</td>
                            <td>
                              {row.hs_record?.url
                                ? <a href={row.hs_record.url} target="_blank" rel="noreferrer">{row.hs_record.id}</a>
                                : row.hs_record?.id || '—'}
                            </td>
                            <td>{getRecordLabel(row)}</td>
                            <td>
                              {fix !== 'hubspot' && <span className="text-muted">{problem.field === 'cc_email' ? 'FF cc: ' : 'FF: '}</span>}
                              <code>{showInvisibleCharacters(problem.value)}</code>
                            </td>
                            <td>
                              {{
                                whitespace: 'Extra or hidden whitespace',
                                multiple_addresses: 'Multiple addresses without a comma/semicolon between them',
                                invalid: 'Not an email address',
                                missing: 'No email in HubSpot',
                              }[problem.reason] || problem.reason}
                            </td>
                            <td>
                              {fix === 'hubspot' && 'Fix in HubSpot, then sync'}
                              {fix === 'no_hubspot_record' && 'No HubSpot record'}
                              {fix === 'resync' && index === 0 && (
                                <>
                                  HubSpot is already correct —{' '}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    style={{ padding: '0 6px', fontSize: '12px' }}
                                    disabled={bulkSyncState.isRunning || !canSyncRecord(row)}
                                    onClick={() => {
                                      setEmailProblemsModalOpen(false);
                                      openSyncPreview(row);
                                    }}
                                  >
                                    Sync Record
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        )))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEmailProblemsModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setEmailProblemsModalOpen(false)} />
        </>
      )}

      {requestLogModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setRequestLogModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Offset</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Returned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requestLog.map((entry) => (
                          <tr
                            key={entry.requestNumber}
                            style={
                              entry.pending ? { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
                              : entry.ok === false ? { backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                              : undefined
                            }
                          >
                            <td>{entry.requestNumber}</td>
                            <td>{entry.offset || 0}</td>
                            <td>
                              {entry.pending
                                ? 'Pending...'
                                : entry.ok ? entry.status : `${entry.status || 'ERR'}${entry.error ? ` (${entry.error})` : ''}`}
                            </td>
                            <td>{entry.pending ? '—' : `${entry.durationMs}ms`}</td>
                            <td>{entry.returned ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRequestLogModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setRequestLogModalOpen(false)} />
        </>
      )}

      {loadError && (
        <div className="alert alert-danger mb-3" role="alert">
          {loadError}
        </div>
      )}

      <div className="border bg-white p-3">
        <div className="border" style={{ flexDirection: 'row', display: 'flex' }}>
          <div style={{ padding: '10px', width: '50%' }}>
            <strong>Sync Status:</strong> <span className="text-muted" style={{ fontSize: '12px' }}>(click a status to filter)</span>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('hs_only')}
              title="Filter to hs_only records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.hs_only || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>hs_only</b> Record exists in Hubspot but not in Fastlane
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('ff_only')}
              title="Filter to ff_only records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.ff_only || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>ff_only</b> Record exists in Fastlane but not in Hubspot
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync')}
              title="Filter to out_of_sync records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>{statusCounts.out_of_sync || 0}</b>
              <b className='border px-1' style={{ backgroundColor: '#f5f5f5', borderRadius: '3px' }}>out_of_sync</b> Record exists in both systems but has real changes beyond the timestamp
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync_timestamp_only')}
              title="Filter to out_of_sync_timestamp_only records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>{statusCounts.out_of_sync_timestamp_only || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>out_of_sync_timestamp_only</b> HubSpot's timestamp changed but nothing we track did — safe to ignore
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('synced')}
              title="Filter to synced records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'white', borderRadius: '3px' }}>{statusCounts.synced || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'white', borderRadius: '3px' }}>synced</b> Record exists in both systems and is in sync
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('pending_merge')}
              title="Filter to pending_merge records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(253, 126, 20, 0.15)', borderRadius: '3px' }}>{statusCounts.pending_merge || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'rgba(253, 126, 20, 0.15)', borderRadius: '3px' }}>pending_merge</b> HubSpot merged duplicates into this record that are still separate brokers here — sync it to merge them
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('merged')}
              title="Filter to merged records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: '#e9ecef', borderRadius: '3px' }}>{statusCounts.merged || 0}</b>
              <b className='border px-1' style={{ backgroundColor: '#e9ecef', borderRadius: '3px' }}>merged</b> Duplicate already merged into another broker (archived, can be undone)
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('orphaned')}
              title="Filter to orphaned records"
            >
              <b className='border px-1' style={{ width: '50px', backgroundColor: 'rgba(220, 53, 69, 0.15)', borderRadius: '3px' }}>{statusCounts.orphaned || 0}</b>
              <b className='border px-1' style={{ backgroundColor: 'rgba(220, 53, 69, 0.15)', borderRadius: '3px' }}>orphaned</b>
              {/* flex: 1 keeps this longer description beside the chip, wrapping in place instead of dropping below it */}
              <span style={{ flex: 1, minWidth: 0 }}>HubSpot company deleted, but a broker with the same email is still synced — leftover duplicate to merge into the live broker (hover the status for details)</span>
            </div>
          </div>
          <div style={{ padding: '10px', width: '50%' }}>
            <strong>Merged:</strong>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
              <b className='border px-1' style={{ borderRadius: '3px' }}>HS Merged IDs</b> Hubspot IDs of records that have been merged into this record.
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px' }}>
              <b className='border px-1' style={{ borderRadius: '3px' }}>HS Merged Parent ID</b> Hubspot ID of the parent record if this record has been merged into.
            </div>
          </div>
        </div>
        <Table
          loadingFFData={loadingFFData}
          loadingHubspotData={loadingHubspotData}
          data={combinedData}
          columns={columns}
          autoResetPageIndex={false}
          renderToolbar={(table) => {
            tableInstanceRef.current = table;

            // pending_merge rows are left out — merging moves leads/sales between brokers,
            // so each one goes through the per-record preview instead.
            const pageSyncableRecords = table.getRowModel().rows
              .map((row) => row.original)
              .filter((row) => canSyncRecord(row) && row.sync_status !== 'pending_merge');

            const showSyncTimestampsOnly = table.getColumn('sync_status')?.getFilterValue() === 'out_of_sync_timestamp_only';
            const showMergeAllOrphaned = table.getColumn('sync_status')?.getFilterValue() === 'orphaned';

            // Every page, not just the current one — getFilteredRowModel respects any
            // other active filters (e.g. an email search).
            const filteredOrphanedRecords = table.getFilteredRowModel().rows
              .map((row) => row.original)
              .filter((row) => row.sync_status === 'orphaned' && row.orphan_of);

            const pageTimestampOnlyRecords = table.getRowModel().rows
              .map((row) => row.original)
              .filter((row) => row.sync_status === 'out_of_sync_timestamp_only' && canSyncRecord(row));

            return (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={bulkSyncState.isRunning || pageSyncableRecords.length === 0}
                  onClick={() => openBulkSyncConfirmation(pageSyncableRecords)}
                >
                  {bulkSyncState.isRunning
                    ? `Syncing Page (${bulkSyncState.completed}/${bulkSyncState.total})`
                    : `Sync This Page (${pageSyncableRecords.length})`}
                </button>
                {showMergeAllOrphaned && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={bulkOrphanMerge.isRunning || filteredOrphanedRecords.length === 0}
                    onClick={() => openBulkOrphanMerge(filteredOrphanedRecords)}
                    title="Merge every orphaned broker matching the current filters (all pages) into its live broker"
                  >
                    {bulkOrphanMerge.isRunning
                      ? 'Merging...'
                      : `Merge All Orphaned (${filteredOrphanedRecords.length})`}
                  </button>
                )}
                {showSyncTimestampsOnly && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={bulkTimestampSyncState.isRunning || pageTimestampOnlyRecords.length === 0}
                    onClick={() => openBulkTimestampSyncConfirmation(pageTimestampOnlyRecords)}
                    title="Updates only the HubSpot timestamp, in a single request, for records where nothing this app tracks has actually changed"
                  >
                    {bulkTimestampSyncState.isRunning
                      ? 'Syncing Timestamps...'
                      : `Sync Timestamps Only (${pageTimestampOnlyRecords.length})`}
                  </button>
                )}
              </div>
            );
          }}
        />
      </div>

      {bulkSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Bulk Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Sync {bulkSyncConfirmation.records.length} records from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This will send one request per row and open the progress modal immediately after you confirm.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkSync}>
                    Start Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkSyncConfirmation} />
        </>
      )}

      {bulkTimestampSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Timestamp Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkTimestampSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Update the HubSpot timestamp for {bulkTimestampSyncConfirmation.records.length} record{bulkTimestampSyncConfirmation.records.length === 1 ? '' : 's'} from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This sends a single batch request and updates only the hs_lastmodifieddate column for each record — no other fields are touched. Each record is re-verified on the server before its timestamp is updated, and any record found to have real changes is skipped rather than synced.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkTimestampSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkTimestampSync}>
                    Sync Timestamps
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkTimestampSyncConfirmation} />
        </>
      )}

      {bulkSyncState.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Bulk Sync Progress</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeBulkSyncModal}
                    aria-label="Close"
                    disabled={bulkSyncState.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{bulkSyncState.completed} of {bulkSyncState.total} complete</strong>
                      <span>{bulkProgressPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${bulkProgressPercent}%` }}
                        aria-valuenow={bulkProgressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div style={{ marginTop: '6px' }}>
                      {bulkSyncState.isRunning
                        ? `Syncing ${bulkSyncState.currentLabel || 'next broker'}...`
                        : 'Bulk sync complete.'}
                    </div>
                  </div>

                  <div>
                    <strong>Failures ({bulkSyncState.failures.length})</strong>
                    <div
                      className="border rounded mt-2 p-2"
                      style={{ maxHeight: '260px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                    >
                      {bulkSyncState.failures.length === 0 ? (
                        <div className="text-muted">No failures yet.</div>
                      ) : (
                        bulkSyncState.failures.map((failure) => (
                          <div key={failure.id} className="border-bottom pb-2 mb-2">
                            <div><strong>{failure.label}</strong></div>
                            <div style={{ whiteSpace: 'pre-line' }}>{failure.message}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBulkSyncModal}
                    disabled={bulkSyncState.isRunning}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop show"
            onClick={bulkSyncState.isRunning ? undefined : closeBulkSyncModal}
          />
        </>
      )}

      {/* Sync Preview Modal */}
      {syncPreview.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {syncPreview.isNewRecord ? 'Create Broker From HubSpot' : 'Sync Broker With HubSpot'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSyncPreview}
                    aria-label="Close"
                    disabled={syncPreview.isSaving}
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {syncPreview.isLoading && <div className="text-muted">Loading preview...</div>}

                  {syncPreview.error && (
                    <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                      {syncPreview.error}
                    </div>
                  )}

                  {!syncPreview.isLoading && !syncPreview.error && syncPreview.pendingMerges.length > 0 && (
                    <div className="alert alert-warning" role="alert">
                      <strong>
                        {syncPreview.pendingMerges.length} duplicate broker{syncPreview.pendingMerges.length === 1 ? '' : 's'} will be merged into this record
                      </strong>
                      <div style={{ fontSize: '13px', margin: '4px 0 8px' }}>
                        HubSpot merged these companies into this one. Their records below will move to this broker, and the duplicates will be archived and marked as merged. Use Undo Merge on a merged row to reverse it.
                      </div>
                      <table className="table table-sm table-bordered bg-white" style={{ marginBottom: 0, fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>FF ID</th>
                            <th>HS ID</th>
                            <th>Name / Email</th>
                            <th>User</th>
                            <th>Leads</th>
                            <th>Sales</th>
                            <th>Territory Checks</th>
                            <th>Manual TCs</th>
                            <th>Sent Emails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.pendingMerges.map((merge) => (
                            <tr key={merge.id}>
                              <td>{merge.id}{merge.archived ? ' (archived)' : ''}</td>
                              <td>{merge.hubspot_id}</td>
                              <td>{merge.display_name}<br /><span className="text-muted">{merge.email}</span></td>
                              <td>{formatDiffValue(merge.user_email)}</td>
                              <td>{merge.record_counts.leads}</td>
                              <td>{merge.record_counts.sales}</td>
                              <td>{merge.record_counts.territory_checks}</td>
                              <td>{merge.record_counts.manual_territory_checks}</td>
                              <td>{merge.record_counts.sent_emails}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!syncPreview.isLoading && !syncPreview.error && syncPreview.fields && (
                    <>
                      {!syncPreview.fields.some((field) => field.changed) && (
                        <div className="text-muted mb-2">No field changes — this record already matches HubSpot.</div>
                      )}
                      <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.fields.map(({ property, from, to, changed }) => (
                            <tr key={property}>
                              <td>{humanizeDiffKey(property)}</td>
                              <td>{formatDiffValue(from)}</td>
                              <td style={changed ? { backgroundColor: 'rgba(40, 167, 69, 0.2)', fontWeight: 600 } : undefined}>
                                {formatDiffValue(to)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeSyncPreview}
                    disabled={syncPreview.isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmSyncPreview}
                    disabled={syncPreview.isLoading || syncPreview.isSaving || Boolean(syncPreview.error) || (!(syncPreview.fields || []).some((field) => field.changed) && syncPreview.pendingMerges.length === 0)}
                  >
                    {syncPreview.isSaving ? 'Saving...' : syncPreview.pendingMerges.length > 0 ? 'Save & Merge' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={syncPreview.isSaving ? undefined : closeSyncPreview} />
        </>
      )}

      {bulkOrphanMerge.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Merge All Orphaned Brokers</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkOrphanMerge}
                    aria-label="Close"
                    disabled={bulkOrphanMerge.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Merge {bulkOrphanMerge.records.length} orphaned {bulkOrphanMerge.records.length === 1 ? 'broker' : 'brokers'} into the live broker with the same email?
                  </p>
                  <p className="text-muted" style={{ marginBottom: '12px' }}>
                    Each one&apos;s leads, sales, territory checks and sent emails move to the live broker, and the orphan is archived. Every merge can be reversed individually with Undo Merge.
                  </p>
                  <div className="border" style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '13px' }}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr><th>Orphan</th><th>Merges into</th></tr>
                      </thead>
                      <tbody>
                        {bulkOrphanMerge.records.map((record) => (
                          <tr key={record.ff_record.id}>
                            <td>FF #{record.ff_record.id} — {record.ff_record.display_name}</td>
                            <td>FF #{record.orphan_of.id} — {record.orphan_of.display_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkOrphanMerge} disabled={bulkOrphanMerge.isRunning}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger" onClick={confirmBulkOrphanMerge} disabled={bulkOrphanMerge.isRunning}>
                    {bulkOrphanMerge.isRunning ? 'Merging...' : `Merge ${bulkOrphanMerge.records.length}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={bulkOrphanMerge.isRunning ? undefined : closeBulkOrphanMerge} />
        </>
      )}

      {mergeOrphanConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Merge Orphaned Broker</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeMergeOrphanConfirmation}
                    aria-label="Close"
                    disabled={mergeOrphanConfirmation.isSaving}
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Merge FF #{mergeOrphanConfirmation.record?.ff_record?.id} ({getRecordLabel(mergeOrphanConfirmation.record)}) into FF #{mergeOrphanConfirmation.record?.orphan_of?.id} ({mergeOrphanConfirmation.record?.orphan_of?.display_name})?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    HubSpot company {mergeOrphanConfirmation.record?.ff_record?.hubspot_id} no longer exists. This broker&apos;s leads, sales, territory checks and sent emails will move to FF #{mergeOrphanConfirmation.record?.orphan_of?.id}, and this broker will be archived. You can reverse this with Undo Merge.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeMergeOrphanConfirmation} disabled={mergeOrphanConfirmation.isSaving}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmMergeOrphan} disabled={mergeOrphanConfirmation.isSaving}>
                    {mergeOrphanConfirmation.isSaving ? 'Merging...' : 'Merge'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={mergeOrphanConfirmation.isSaving ? undefined : closeMergeOrphanConfirmation} />
        </>
      )}

      {undoMergeConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Undo Merge</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeUndoMergeConfirmation}
                    aria-label="Close"
                    disabled={undoMergeConfirmation.isSaving}
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Undo the merge of FF #{undoMergeConfirmation.record?.ff_record?.id} ({getRecordLabel(undoMergeConfirmation.record)}) into FF #{undoMergeConfirmation.record?.ff_record?.merged_into_id}?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    The leads, sales, territory checks and sent emails the merge moved will go back to this broker, and its archived status will be restored. HubSpot still lists it as merged, so it will be flagged &quot;Merge undone&quot; and future syncs won&apos;t merge it again.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeUndoMergeConfirmation} disabled={undoMergeConfirmation.isSaving}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-warning" onClick={confirmUndoMerge} disabled={undoMergeConfirmation.isSaving}>
                    {undoMergeConfirmation.isSaving ? 'Undoing...' : 'Undo Merge'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={undoMergeConfirmation.isSaving ? undefined : closeUndoMergeConfirmation} />
        </>
      )}

      {modalMessage && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Sync Failed</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setModalMessage(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{modalMessage}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalMessage(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setModalMessage(null)} />
        </>
      )}

      {hubspotJsonModal && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">HubSpot JSON</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setHubspotJsonModal(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(hubspotJsonModal, null, 2)}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setHubspotJsonModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setHubspotJsonModal(null)} />
        </>
      )}

      {successToastMessage && (
        <div
          className="alert alert-success shadow"
          role="status"
          style={{
            position: 'fixed',
            right: '16px',
            bottom: '16px',
            margin: 0,
            zIndex: 2000,
            minWidth: '280px',
            maxWidth: '420px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{successToastMessage}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccessToastMessage(null)}
          />
        </div>
      )}
    </>
  );
};

const element = document.getElementById('brokers-table');
if (element) {
  const root = createRoot(element);
  root.render(<BrokersTable/>);
}